/**
 * Layout pieces every iManage page shares: the page header (a breadcrumb or
 * title, then controls), the scoped search field, and the split between the
 * document table and the preview pane.
 *
 * On wide screens the preview sits beside the table; below `lg` it covers the
 * table, with its own close button. Esc closes it; `/` focuses the search.
 *
 * @module components/apps/imanage/IManageLayout
 */
import type { HairlineProps } from "@lucasmarkes/hairline/react";
import { SearchIcon, XIcon } from "lucide-react";
import { useEffect, useRef, useState, type ComponentType, type ReactNode } from "react";

import { cn } from "~/lib/utils";
import { COLLAPSED_SIDEBAR_TITLEBAR_INSET_CLASS } from "~/workspaceTitlebar";
import type { SegmentedTab } from "../../regulation/PageHeader";
import { isTyping } from "../../regulation/finding/FindingsQueue";
import { HairlineArt } from "../../ui/hairline-art";
import { Kbd } from "../../ui/kbd";
import { Tabs, TabsList, TabsTab } from "../../ui/tabs";

export function IManagePageHeader({
  title,
  actions,
  children,
}: {
  /** The breadcrumb or page title. */
  readonly title: ReactNode;
  readonly actions?: ReactNode;
  /** A second row (tabs). */
  readonly children?: ReactNode;
}) {
  return (
    <header className="shrink-0 border-b border-border/60" data-imanage-header="">
      <div
        className={cn(
          "flex min-h-[52px] flex-wrap items-center gap-x-3 gap-y-2 px-4 py-2 sm:flex-nowrap sm:px-5",
          COLLAPSED_SIDEBAR_TITLEBAR_INSET_CLASS,
        )}
      >
        <div className="flex min-w-0 flex-1 items-center gap-2">{title}</div>
        {actions ? (
          <div className="flex shrink-0 items-center gap-1.5 max-sm:w-full max-sm:[&>form]:flex-1">
            {actions}
          </div>
        ) : null}
      </div>
      {children ? (
        <div className="-mt-1.5 flex items-end gap-3 overflow-x-auto px-4 [scrollbar-width:none] sm:px-5 [&::-webkit-scrollbar]:hidden">
          {children}
        </div>
      ) : null}
    </header>
  );
}

/**
 * The views of one page (a workspace's Documents · Emails · Folders, a
 * document's Preview · Details · Versions): underline tabs that sit on the
 * line below them, so they read as the page's sections, not as a switch.
 */
export function IManageViewTabs<T extends string>({
  label,
  value,
  onValueChange,
  tabs,
  className,
}: {
  readonly label: string;
  readonly value: T;
  readonly onValueChange: (value: T) => void;
  readonly tabs: ReadonlyArray<SegmentedTab<T>>;
  readonly className?: string;
}) {
  return (
    <Tabs value={value} onValueChange={(next) => onValueChange(next as T)} className={cn("min-w-0", className)}>
      <TabsList aria-label={label} className="gap-4 overflow-visible border-0" data-imanage-view-tabs="">
        {tabs.map((tab) => (
          <TabsTab
            key={tab.value}
            value={tab.value}
            className="mb-0 flex h-9 items-center gap-1.5 rounded-none px-0 pt-0 pb-px text-[13px] leading-5 font-normal hover:bg-transparent data-[active]:font-medium aria-selected:font-medium"
          >
            {tab.label}
            {tab.count !== undefined ? (
              <span className="text-[11px] font-normal tabular-nums text-muted-foreground">{tab.count}</span>
            ) : null}
          </TabsTab>
        ))}
      </TabsList>
    </Tabs>
  );
}

/**
 * A search box that commits on Enter (and clears on Esc), so each search is one
 * request and one history entry, not one per keystroke.
 */
export function IManageSearchField({
  value,
  onCommit,
  placeholder,
  autoFocus = false,
  className,
}: {
  readonly value: string;
  readonly onCommit: (value: string) => void;
  readonly placeholder: string;
  readonly autoFocus?: boolean;
  readonly className?: string;
}) {
  const [draft, setDraft] = useState(value);
  const input = useRef<HTMLInputElement>(null);
  useEffect(() => setDraft(value), [value]);
  useEffect(() => {
    const onKey = (event: KeyboardEvent) => {
      if (event.key !== "/" || event.metaKey || event.ctrlKey || isTyping(event.target)) return;
      event.preventDefault();
      input.current?.focus();
      input.current?.select();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);
  return (
    <form
      role="search"
      className={cn(
        "flex h-7 w-full items-center gap-1.5 rounded-md border border-border/70 bg-background px-2 text-[13px] transition-colors focus-within:border-ring focus-within:ring-2 focus-within:ring-ring/25",
        className,
      )}
      onSubmit={(event) => {
        event.preventDefault();
        onCommit(draft.trim());
      }}
    >
      <SearchIcon aria-hidden className="size-3.5 shrink-0 text-muted-foreground" />
      <input
        ref={input}
        // oxlint-disable-next-line jsx-a11y/no-autofocus -- the Search page exists to type into
        autoFocus={autoFocus}
        aria-label={placeholder}
        value={draft}
        maxLength={500}
        placeholder={placeholder}
        onChange={(event) => setDraft(event.target.value)}
        onKeyDown={(event) => {
          if (event.key === "Escape" && draft) {
            event.preventDefault();
            event.stopPropagation();
            setDraft("");
            onCommit("");
          }
        }}
        className="min-w-0 flex-1 bg-transparent outline-none placeholder:text-muted-foreground/80"
        data-imanage-search=""
      />
      {draft ? (
        <button
          type="button"
          aria-label="Clear search"
          className="flex size-5 items-center justify-center rounded-sm text-muted-foreground hover:text-foreground"
          onClick={() => {
            setDraft("");
            onCommit("");
          }}
        >
          <XIcon aria-hidden className="size-3.5" />
        </button>
      ) : (
        <Kbd className="h-4 min-w-4 px-1 text-[10px]">/</Kbd>
      )}
    </form>
  );
}

export function IManageSplit({
  preview,
  onClosePreview,
  children,
}: {
  readonly preview: ReactNode | null;
  readonly onClosePreview: () => void;
  readonly children: ReactNode;
}) {
  const open = preview !== null;
  useEffect(() => {
    if (!open) return;
    const onKey = (event: KeyboardEvent) => {
      if (event.key !== "Escape" || event.defaultPrevented || isTyping(event.target)) return;
      onClosePreview();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, onClosePreview]);
  return (
    <div className="relative flex min-h-0 flex-1">
      <div
        className={cn("min-h-0 min-w-0 flex-1 overflow-y-auto", open && "max-lg:invisible")}
        data-imanage-list=""
      >
        {children}
      </div>
      {open ? (
        <div className="absolute inset-0 z-20 flex min-h-0 border-border/60 bg-background lg:static lg:z-auto lg:w-[min(46%,600px)] lg:min-w-[380px] lg:border-l">
          {preview}
        </div>
      ) : null}
    </div>
  );
}

/** A list with nothing in it yet draws a figure; a search or a filter that found nothing keeps a small icon. */
type EmptyListMedia =
  | { readonly art: ComponentType<HairlineProps>; readonly icon?: never }
  | { readonly icon: ReactNode; readonly art?: never };

/** A centered note for an empty list. */
export function EmptyList({
  art,
  icon,
  title,
  body,
  action,
}: EmptyListMedia & {
  readonly title: string;
  readonly body?: string;
  readonly action?: ReactNode;
}) {
  return (
    <div className="flex flex-col items-center px-6 py-16 text-center" data-imanage-empty="">
      {art ? (
        <HairlineArt figure={art} className="mb-4 w-48 sm:w-56" />
      ) : (
        <span className="mb-3 flex size-9 items-center justify-center rounded-lg border border-border/70 text-muted-foreground [&_svg]:size-4.5">
          {icon}
        </span>
      )}
      <p className="text-sm font-medium">{title}</p>
      {body ? <p className="mt-1 max-w-sm text-[13px] text-pretty text-muted-foreground">{body}</p> : null}
      {action ? <div className="mt-4">{action}</div> : null}
    </div>
  );
}
